/**
 * STATISTICS' "OVERVIEW" TAB, REDRAWN (design 18; the owner, 2026-10-05: "its bad shaped on UI/UX
 * and components ... maybe like charts and different components / bars"). Thirteen equal tiles
 * became, top to bottom:
 *
 *   1. THE PERIOD - one button in the tabs' row (design 24, T15), and what it is compared with.
 *   2. THE HEADLINE CARD - five figures as buttons (Views, Countable views, Unique readers, Likes,
 *      Comments; the kit's `FigureSwitch`), each with its change and its (i), and under them the
 *      pressed figure DAY BY DAY (./statistics/DayChart.tsx): the period's bars, the period before
 *      as a dashed line, a dot on each day an article went out, "Show as a table".
 *   3. TWO CARDS - the five articles that did best by the pressed figure ("Most views", "Most
 *      liked"; ruling 4 - Readers ranks by views and says why), and "Also in these 30 days": every
 *      other figure of the period as a list, each with its change.
 *   4. "WHERE YOUR VIEWS COME FROM" as bars - only once the apps report it.
 *   5. ONE QUIET LINE naming what nothing measures yet, in place of a box each.
 *
 * THE SAME THREE READS AS BEFORE for the figures (routes/dashboard/DashboardRoute.tsx, its header):
 * `getDashboard` with no range, for its `to` alone - handed in by ./ArticleStatsRoute.tsx, which
 * shares it with the Audience tab; then the chosen period, `rangeOf(choice, to)`, and the same
 * number of days before it, `previousPeriod` (../../features/cabinet/ranges.ts, no clock). "Last 30
 * days" first. A change is `changeBetween(now, before)` (none when either figure is unknown or the
 * period before had nothing; "6 fewer" on a base under 50 - ruling 3). While a newly chosen period
 * is read, the last period's figures stay under "Refreshing…" with no change drawn.
 * AND THREE MORE: `getDashboardDays` for the period and for the period before (the bars and the
 * dashed line; a server without the read answers 404 and the card simply has no chart), and every
 * row of `listArticleStats` over the period - RANKED for the list and read for the publication
 * dots; ranking orders rows, nothing adds two of them together.
 *
 * EVERY FIGURE KEEPS ITS STATE: `ok` is the number; `no_data` is a TRUE 0 - except for an average
 * and a share, where "nothing to average" says "Nothing yet"; `not_reported_yet` is never a box: it
 * is a name in the quiet line (Medium, beehiiv: hold the space back until a figure means something).
 *
 * WHAT IT KEEPS: both view numbers, labelled (Countable views with its banknote and Beta, the number pay is
 * worked out from); Readers is the range's own distinct count, never a sum of days; tips and
 * supporter messages are COUNTS (the money is on Payments) with the way to read and reply; followers
 * gained and lost, and no follower list anywhere; read-through with its coverage once measured;
 * All-time views, every view ever from both apps (`allTimeViewsOf`), with no change - all time has
 * no period before it (the owner, 2026-10-05: "one app, one data"). The sentence for a period with
 * nothing counted at all stays at the top. Plain words: no "UTC", no "platform", no "pay uses".
 */
import { Anchor, Group, Paper, Popover, SimpleGrid, Skeleton, Stack, Text } from '@mantine/core';
import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Banknote, Info, PeriodPicker } from '@moonx/ui';
import { useCabinetApi } from '../../features/cabinet/api';
import type { CabinetApi, Dashboard, MetricTile } from '../../features/cabinet/api';
import { allTimeViewsOf } from '../../features/cabinet/all-time-views';
import {
  BarList,
  ErrorState,
  FigureList,
  FigureSwitch,
  LoadingState,
  PartialFailureNote,
  RefreshingNote,
  StatCard,
} from '../../features/cabinet/components';
import type {
  ListedBar,
  ListedFigure,
  StatDelta,
  SwitchFigure,
} from '../../features/cabinet/components';
import {
  formatCount,
  formatDayRange,
  formatPercent,
  formatPeriod,
} from '../../features/cabinet/format';
import {
  PERIOD_LABELS,
  changeBetween,
  daysIn,
  isDay,
  periodButtonFor,
  previousPeriod,
  rangeOf,
} from '../../features/cabinet/ranges';
import type { DayRange, PeriodChoice } from '../../features/cabinet/ranges';
import { useAsync } from '../../features/cabinet/state';
import type { AsyncResult } from '../../features/cabinet/state';
import { InAppAnchor } from '../../features/shell/InAppAnchor';
import { useTranslation } from '../../i18n';
import { MESSAGES_PATH } from '../messages';
import { editorHrefFor } from './EditorRoute';
import type { ArticlesKey, ArticlesT } from './articles-i18n';
import { DayChart } from './statistics/DayChart';
import {
  HEADLINE_FIGURES,
  counted,
  dashboardTile,
  dayValue,
  figureBadge,
  figureLabel,
  figureMost,
  figureNoneOnDays,
  figureOf,
  periodWords,
  rankedBy,
  rowTile,
} from './statistics/figures';
import type { ArticleFigureKey, FigureKey, PeriodWords } from './statistics/figures';
import { answers, daysKey, useAllArticleStats, useDays } from './statistics/reads';
import type { AllArticleStats } from './statistics/reads';
import classes from './articles.module.css';

export interface StatsOverviewProps {
  /** `getDashboard` with no range - read once by the screen, for its `to`. */
  readonly latest: AsyncResult<Dashboard>;
  /** "All articles": the Articles tab on this period, sorted by this figure. */
  readonly onAllArticles?: (period: PeriodChoice, sortBy: ArticleFigureKey) => void;
  /** Where the period button goes - the tabs' row; `null` while the tab is not shown. */
  readonly pickerSlot?: HTMLElement | null | undefined;
}

export function StatsOverview({ latest, onAllArticles, pickerSlot }: StatsOverviewProps) {
  const { t } = useTranslation('articles');
  if (latest.status === 'error' && latest.data === undefined) {
    return (
      <ErrorState
        title={t('common.figuresDidNotLoad')}
        error={latest.error}
        onRetry={latest.refetch}
      />
    );
  }
  if (latest.data === undefined) {
    return (
      <Stack gap="sm">
        <LoadingState label={t('stats.overview.reading')} />
        <OverviewSkeleton />
      </Stack>
    );
  }
  if (isDay(latest.data.to)) {
    return (
      <PeriodFigures
        latestDay={latest.data.to}
        onAllArticles={onAllArticles}
        pickerSlot={pickerSlot}
      />
    );
  }
  // A `to` this app cannot count back from: no period button, no chart, the usual period's figures.
  return (
    <Stack gap="lg">
      <Text className={classes.range}>{formatDayRange(latest.data.from, latest.data.to)}</Text>
      <Figures
        now={latest.data}
        before={undefined}
        choice={DEFAULT_PERIOD}
        range={{ from: latest.data.from, to: latest.data.to }}
        onAllArticles={undefined}
      />
    </Stack>
  );
}

/* ------------------------------------------------------------------------------ the period */

/** The period chosen when the tab opens - the Overview screen's. */
const DEFAULT_PERIOD: PeriodChoice = { kind: 'last30' };

/** One read of a period: the days asked for, and the answer. */
interface PeriodReading {
  readonly range: DayRange;
  readonly dashboard: Dashboard;
}

function sameDays(a: DayRange, b: DayRange): boolean {
  return a.from === b.from && a.to === b.to;
}

/** `getDashboard` over exactly `range`; a new range aborts the read of the last one. */
function usePeriodReading(api: CabinetApi, range: DayRange): AsyncResult<PeriodReading> {
  const read = useCallback(
    async (signal: AbortSignal): Promise<PeriodReading> => ({
      range,
      dashboard: await api.getDashboard({ from: range.from, to: range.to }, signal),
    }),
    [api, range],
  );
  return useAsync(read, `statistics:${range.from}..${range.to}`);
}

/** The quiet line under the row - the Overview screen's words, so the two say it alike. */
function periodNote(choice: PeriodChoice, range: DayRange, t: ArticlesT): string {
  const days = daysIn(range);
  const counted = { count: days, value: formatCount(days) };
  if (choice.kind === 'thisMonth') {
    const before =
      days === 1
        ? t('stats.period.dayBeforeInSentence')
        : t('stats.period.daysBeforeInSentence', counted);
    const month = t('stats.period.monthSoFar', { month: formatPeriod(range.to.slice(0, 7)) });
    return t('stats.period.comparedWith', { period: month, before });
  }
  const name = choice.kind === 'custom' ? t('stats.period.custom') : PERIOD_LABELS[choice.kind];
  const before =
    days === 1 ? t('stats.period.dayBeforeInSentence') : t('stats.period.previousDays', counted);
  return t('stats.period.comparedWith', { period: name, before });
}

function PeriodFigures({
  latestDay,
  onAllArticles,
  pickerSlot,
}: {
  readonly latestDay: string;
  readonly onAllArticles: StatsOverviewProps['onAllArticles'];
  readonly pickerSlot: StatsOverviewProps['pickerSlot'];
}) {
  const { t } = useTranslation('articles');
  const api = useCabinetApi();
  const [choice, setChoice] = useState<PeriodChoice>(DEFAULT_PERIOD);
  const range = useMemo(() => rangeOf(choice, latestDay), [latestDay, choice]);
  const before = useMemo(() => previousPeriod(range), [range]);

  const current = usePeriodReading(api, range);
  const previous = usePeriodReading(api, before);

  // `useAsync` keeps the last answer while a new key is read: it may be the LAST period's.
  const shown = current.data;
  const fresh = shown !== undefined && sameDays(shown.range, range);
  const comparable =
    fresh && previous.data !== undefined && sameDays(previous.data.range, before)
      ? previous.data.dashboard
      : undefined;
  const reading = !fresh && shown !== undefined && current.status !== 'error';
  // One button for the period (design 24, T15), in the tabs' row; here when there is no row.
  const picker = (
    <PeriodPicker
      label={t('stats.period.picker')}
      {...periodButtonFor(choice, latestDay, setChoice)}
    />
  );

  return (
    <Stack gap="lg">
      <Stack gap={8}>
        {pickerSlot === undefined ? picker : pickerSlot && createPortal(picker, pickerSlot)}
        <Group gap="sm" wrap="wrap">
          <Text className={classes.periodNote}>{periodNote(choice, range, t)}</Text>
          {reading || (fresh && comparable === undefined && previous.status !== 'error') ? (
            <RefreshingNote label={t('common.refreshing')} />
          ) : null}
        </Group>
      </Stack>

      {current.status === 'error' && !fresh ? (
        <ErrorState
          title={t('common.figuresDidNotLoad')}
          error={current.error}
          onRetry={current.refetch}
        />
      ) : shown === undefined ? (
        <OverviewSkeleton />
      ) : (
        <>
          {fresh && previous.status === 'error' ? (
            // The figures stand without their changes; one note says why there are none.
            <PartialFailureNote what={t('stats.overview.compareFigures')} error={previous.error} />
          ) : null}
          <Figures
            now={shown.dashboard}
            before={comparable}
            choice={choice}
            range={fresh ? range : shown.range}
            onAllArticles={onAllArticles}
          />
        </>
      )}
    </Stack>
  );
}

function OverviewSkeleton() {
  return (
    <Stack gap="md">
      <Skeleton height={300} radius="xl" />
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Skeleton height={200} radius="xl" />
        <Skeleton height={200} radius="xl" />
      </SimpleGrid>
    </Stack>
  );
}

/* ----------------------------------------------------------------------------- the figures */

/** A share's change in points - "3 pp" beside its arrow - never a percentage of a percentage. */
function pointsBetween(
  current: number | undefined,
  previous: number | undefined,
  t: ArticlesT,
): StatDelta | undefined {
  if (current === undefined || previous === undefined) {
    return undefined;
  }
  const points = Math.round((current - previous) * 100);
  if (points === 0) {
    return {
      text: t('stats.overview.points.noChange'),
      tone: 'flat',
      says: t('stats.overview.points.same'),
    };
  }
  const size = Math.abs(points);
  return points > 0
    ? {
        text: t('stats.overview.points.short', { count: size }),
        tone: 'up',
        says: t('stats.overview.points.more', { count: size }),
      }
    : {
        text: t('stats.overview.points.short', { count: size }),
        tone: 'down',
        says: t('stats.overview.points.less', { count: size }),
      };
}

/** A count's change between two tiles, `undefined` when there is no period before. */
function countChange(now: MetricTile, before: MetricTile | undefined): StatDelta | undefined {
  return before === undefined ? undefined : changeBetween(figureOf(now), figureOf(before));
}

/** The first headline figure the period has anything of - Views unless it has none. */
function firstWithAnything(now: Dashboard): FigureKey {
  return HEADLINE_FIGURES.find((key) => (figureOf(dashboardTile(now, key)) ?? 0) > 0) ?? 'views';
}

interface FiguresProps {
  readonly now: Dashboard;
  readonly before: Dashboard | undefined;
  readonly choice: PeriodChoice;
  /** The days `now` answers - the ranked list and the chart read the same. */
  readonly range: DayRange;
  readonly onAllArticles: StatsOverviewProps['onAllArticles'];
}

function Figures({ now, before, choice, range, onAllArticles }: FiguresProps) {
  const { t } = useTranslation('articles');
  const [chosen, setChosen] = useState<FigureKey | null>(null);
  const figure = chosen ?? firstWithAnything(now);
  // A custom range is worded by its days, as the usual period is.
  const words = periodWords(choice.kind === 'custom' ? 'usual' : choice.kind, range, t);
  const tiles = now.tiles;

  // Nothing counted in these days at all: without a word a new author sees honest zeroes and no
  // idea whether they simply have no readers yet - so one quiet line says so.
  const everythingEmpty =
    tiles.views.state !== 'ok' &&
    tiles.countedViews.state !== 'ok' &&
    tiles.likes.state !== 'ok' &&
    tiles.comments.state !== 'ok' &&
    tiles.tips.state !== 'ok';

  const headline: SwitchFigure<FigureKey>[] = HEADLINE_FIGURES.map((key) => {
    const tile = dashboardTile(now, key);
    const value = figureOf(tile);
    return {
      key,
      label: figureLabel(key, t),
      badge: figureBadge(key, t),
      ...(key === 'countedViews' ? { icon: <Banknote size={14} aria-hidden /> } : {}),
      info: <FigureInfo name={figureLabel(key, t)}>{t(`stats.info.${key}`)}</FigureInfo>,
      value: value === undefined ? notShown(tile, t) : formatCount(value),
      quiet: value === undefined,
      change: countChange(tile, before === undefined ? undefined : dashboardTile(before, key)),
    };
  });

  const unmeasured = notMeasuredNames(now, t);
  // Every row of the period, read once: ranked for the list, and the publication days for the dots.
  const stats = useAllArticleStats(range);
  const statsHere = answers(stats.data, range) ? stats.data : undefined;

  return (
    <Stack gap="md">
      {everythingEmpty ? (
        <Text size="sm" c="dimmed">
          {t('stats.overview.nothingCounted')}
        </Text>
      ) : null}

      <Paper
        className={classes.headline}
        component="section"
        aria-label={t('stats.overview.figuresLabel')}
        pos="relative"
      >
        <FigureSwitch
          label={t('common.showOnChart')}
          figures={headline}
          pressed={figure}
          onPress={setChosen}
        />
        <div className={classes.headlineChart}>
          <FigureDays figure={figure} range={range} words={words} stats={statsHere} />
        </div>
      </Paper>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md" className={classes.lowerCards}>
        <MostList
          figure={figure}
          choice={choice}
          words={words}
          stats={stats}
          here={statsHere}
          onAll={onAllArticles}
        />
        <StatCard title={t('stats.overview.alsoTitle', { period: words.inThese })}>
          <FigureList figures={alsoFigures(now, before, t)} />
        </StatCard>
      </SimpleGrid>

      <Sources now={now} />

      {unmeasured.length === 0 ? null : (
        <Text className={classes.periodNote}>
          {t('stats.overview.notMeasured', { list: sentenceList(unmeasured, t) })}
        </Text>
      )}
    </Stack>
  );
}

/** The words a figure with no number shows in its place. */
function notShown(tile: MetricTile, t: ArticlesT): string {
  return tile.state === 'not_reported_yet'
    ? t('common.notMeasuredYet')
    : tile.state === 'no_data'
      ? t('common.nothingYet')
      : t('common.notMeasuredYet');
}

/* ------------------------------------------------------------------------- the day by day */

function FigureDays({
  figure,
  range,
  words,
  stats,
}: {
  readonly figure: FigureKey;
  readonly range: DayRange;
  readonly words: PeriodWords;
  /** The period's rows, once they answer this period - for the publication dots. */
  readonly stats: AllArticleStats | undefined;
}) {
  const { t } = useTranslation('articles');
  const before = useMemo(() => previousPeriod(range), [range]);
  const now = useDays(range);
  const earlier = useDays(before);

  const answer = now.data !== undefined && now.data.key === daysKey(range) ? now.data : undefined;
  if (answer === undefined) {
    return now.status === 'error' ? (
      <ErrorState title={t('common.daysDidNotLoad')} error={now.error} onRetry={now.refetch} />
    ) : (
      <Skeleton height={230} radius="md" aria-hidden="true" />
    );
  }
  if (answer.days === null) {
    // A server from before the read: the figures stand alone, with nothing in the chart's place.
    return null;
  }

  const beforeAnswer =
    earlier.data !== undefined && earlier.data.key === daysKey(before) ? earlier.data.days : null;
  const published =
    stats === undefined
      ? undefined
      : new Set(
          stats.items.flatMap((row) =>
            row.publishedAt === null ? [] : [row.publishedAt.slice(0, 10)],
          ),
        );

  return (
    <DayChart
      title={figureLabel(figure, t)}
      worded={(value) => counted(value, figure, t)}
      days={answer.days.days.map((day) => ({ day: day.day, value: dayValue(day, figure) }))}
      before={beforeAnswer?.days.map((day) => dayValue(day, figure))}
      published={published}
      these={words.these}
      beforeWords={words.before}
      beforeInSentence={words.beforeInSentence}
      empty={figureNoneOnDays(figure, t)}
    />
  );
}

/* --------------------------------------------------------------------- the ranked articles */

const MOST_SHOWN = 5;

function MostList({
  figure,
  choice,
  words,
  stats,
  here,
  onAll,
}: {
  readonly figure: FigureKey;
  readonly choice: PeriodChoice;
  readonly words: PeriodWords;
  readonly stats: AsyncResult<AllArticleStats>;
  /** `stats`' answer when it is this period's; `undefined` while it is read. */
  readonly here: AllArticleStats | undefined;
  readonly onAll: StatsOverviewProps['onAllArticles'];
}) {
  const { t } = useTranslation('articles');
  const by = rankedBy(figure);
  const title = t('stats.overview.mostIn', { most: figureMost(figure, t), period: words.inThese });
  const action =
    onAll === undefined ? undefined : (
      <Anchor
        component="button"
        type="button"
        size="xs"
        fw={700}
        onClick={() => {
          onAll(choice, by);
        }}
      >
        {t('stats.overview.allArticles')}
      </Anchor>
    );

  if (here === undefined) {
    return (
      <StatCard title={title} action={action}>
        {stats.status === 'error' ? (
          <ErrorState title={t('list.didNotLoad')} error={stats.error} onRetry={stats.refetch} />
        ) : (
          <Stack gap={8} aria-hidden="true">
            <Skeleton height={28} />
            <Skeleton height={28} />
            <Skeleton height={28} />
          </Stack>
        )}
      </StatCard>
    );
  }

  const ranked = here.items
    .flatMap((row) => {
      const value = figureOf(rowTile(row, by));
      return row.publishedAt === null || value === undefined || value === 0 ? [] : [{ row, value }];
    })
    .sort(
      (a, b) =>
        b.value - a.value || (b.row.publishedAt ?? '').localeCompare(a.row.publishedAt ?? ''),
    )
    .slice(0, MOST_SHOWN);
  const top = ranked[0]?.value ?? 1;
  const bars: ListedBar[] = ranked.map(({ row, value }) => ({
    key: row.articleId,
    label: (
      <InAppAnchor href={editorHrefFor(row.articleId)} size="sm" c="inherit" underline="always">
        {row.title.trim() === '' ? t('common.untitled') : row.title}
      </InAppAnchor>
    ),
    value: counted(value, by, t),
    share: value / top,
  }));

  return (
    <StatCard title={title} action={action}>
      {bars.length === 0 ? (
        <Text size="sm" c="dimmed">
          {noneRanked(by, words, t)}
        </Text>
      ) : (
        <Stack gap={6}>
          <BarList bars={bars} label={title} />
          {figure === 'readers' ? (
            <Text size="xs" c="dimmed">
              {t('stats.overview.readersByViews')}
            </Text>
          ) : null}
        </Stack>
      )}
    </StatCard>
  );
}

function noneRanked(by: ArticleFigureKey, words: PeriodWords, t: ArticlesT): string {
  return t(`stats.overview.noneRanked.${by}`, { period: words.inThese });
}

/* ------------------------------------------------------------------------ also in the period */

function alsoFigures(
  now: Dashboard,
  before: Dashboard | undefined,
  t: ArticlesT,
): readonly ListedFigure[] {
  const tiles = now.tiles;
  const earlier = before?.tiles;
  // Each row with its (i): what the figure means, in one sentence.
  const listed: ListedFigure[] = [];
  const add = (figure: ListedFigure, info: ArticlesKey): void => {
    listed.push(withInfo(figure, t(info)));
  };

  const allTime = allTimeViewsOf(now);
  if (allTime !== undefined) {
    // Every view ever, both apps: no period before it, so no change (the owner, 2026-10-05).
    add(
      {
        label: t('stats.overview.alsoList.allTime'),
        value: formatCount(allTime),
        note: t('stats.overview.alsoList.allTimeNote'),
      },
      'stats.info.allTime',
    );
  }

  const counts: readonly {
    label: string;
    info: ArticlesKey;
    tile: MetricTile;
    was: MetricTile | undefined;
    note?: ReactNode;
  }[] = [
    {
      label: t('stats.overview.alsoList.saves'),
      info: 'stats.info.saves',
      tile: tiles.saves,
      was: earlier?.saves,
    },
    {
      label: t('stats.overview.alsoList.shares'),
      info: 'stats.info.shares',
      tile: tiles.shares,
      was: earlier?.shares,
    },
    {
      label: t('stats.overview.alsoList.tips'),
      info: 'stats.info.tips',
      tile: tiles.tips,
      was: earlier?.tips,
      note: t('stats.overview.alsoList.tipsNote'),
    },
    {
      label: t('stats.overview.alsoList.messages'),
      info: 'stats.info.messages',
      tile: tiles.supporterMessages,
      was: earlier?.supporterMessages,
      // The count used to point at messages nobody could open (register R-006, U1).
      note: (
        <InAppAnchor href={MESSAGES_PATH} size="xs">
          {t('stats.overview.alsoList.messagesLink')}
        </InAppAnchor>
      ),
    },
  ];
  for (const { label, info, tile, was, note } of counts) {
    if (tile.state === 'not_reported_yet') {
      continue;
    }
    add(
      {
        label,
        value: formatCount(figureOf(tile) ?? 0),
        change: countChange(tile, was),
        ...(note === undefined ? {} : { note }),
      },
      info,
    );
  }

  const followers = tiles.followers;
  if (followers.state !== 'not_reported_yet') {
    const gained = followers.gainedInRange;
    const lost = followers.lostInRange;
    add(
      {
        label: t('stats.overview.alsoList.followers'),
        value: formatCount(figureOf(followers) ?? 0),
        ...(followers.state === 'ok' && typeof gained === 'number'
          ? {
              note: t('stats.overview.alsoList.followersNote', {
                gained: formatCount(gained),
                lost: formatCount(lost ?? 0),
              }),
            }
          : {}),
      },
      'stats.info.followers',
    );
  }

  const reading = tiles.averageReadingTime;
  if (reading.state !== 'not_reported_yet') {
    const millis = reading.state === 'ok' ? (reading.millis ?? undefined) : undefined;
    const was =
      earlier?.averageReadingTime.state === 'ok'
        ? (earlier.averageReadingTime.millis ?? undefined)
        : undefined;
    add(
      {
        label: t('stats.overview.alsoList.readingTime'),
        value: millis === undefined ? t('common.nothingYet') : duration(millis, t),
        quiet: millis === undefined,
        change: changeBetween(millis, was, { asCount: false }),
      },
      'stats.info.readingTime',
    );
  }

  const through = tiles.readThrough;
  if (through.state !== 'not_reported_yet') {
    const ratio = through.state === 'ok' ? (through.ratio ?? undefined) : undefined;
    const was =
      earlier?.readThrough.state === 'ok' ? (earlier.readThrough.ratio ?? undefined) : undefined;
    const coverage = through.state === 'ok' ? through.coverageRatio : undefined;
    add(
      {
        label: t('stats.overview.alsoList.readThrough'),
        value: ratio === undefined ? t('common.nothingYet') : formatPercent(ratio),
        quiet: ratio === undefined,
        change: pointsBetween(ratio, was, t),
        // The denominator travels with the number: a share of a fifth of the readers says less.
        ...(typeof coverage === 'number'
          ? {
              note: t('stats.overview.alsoList.readThroughNote', {
                share: formatPercent(coverage),
              }),
            }
          : {}),
      },
      'stats.info.readThrough',
    );
  }

  return listed;
}

function duration(millis: number, t: ArticlesT): string {
  const seconds = Math.round(millis / 1000);
  const minutes = Math.floor(seconds / 60);
  return minutes === 0
    ? t('stats.overview.duration.seconds', { seconds: formatCount(seconds) })
    : t('stats.overview.duration.minutes', {
        minutes: formatCount(minutes),
        seconds: String(seconds % 60),
      });
}

/** The names of what nothing measures yet, for the quiet line - in the order they appear above. */
function notMeasuredNames(now: Dashboard, t: ArticlesT): readonly string[] {
  const tiles = now.tiles;
  const names: string[] = [];
  if (tiles.averageReadingTime.state === 'not_reported_yet') {
    names.push(t('stats.overview.unmeasured.readingTime'));
  }
  if (tiles.readThrough.state === 'not_reported_yet') {
    names.push(t('stats.overview.unmeasured.readThrough'));
  }
  if (tiles.shares.state === 'not_reported_yet') {
    names.push(t('stats.overview.unmeasured.shares'));
  }
  if (tiles.followers.state === 'not_reported_yet') {
    names.push(t('stats.overview.unmeasured.followers'));
  }
  if (tiles.sources.state === 'not_reported_yet') {
    names.push(t('stats.overview.unmeasured.sources'));
  }
  return names;
}

/** "a, b and c". */
function sentenceList(items: readonly string[], t: ArticlesT): string {
  if (items.length <= 1) {
    return items.join('');
  }
  return t('stats.overview.listAnd', {
    rest: items.slice(0, -1).join(t('stats.overview.listComma')),
    last: items[items.length - 1] ?? '',
  });
}

/* ------------------------------------------------------------------------- where they came from */

/** What a way in is called, for the few the lookup holds; anything newer in its own words. */
const SOURCE_NAMES: Readonly<Record<string, ArticlesKey>> = {
  unknown: 'stats.overview.sources.unknown',
  feed: 'stats.overview.sources.feed',
  trending: 'stats.overview.sources.trending',
  deep_link: 'stats.overview.sources.deepLink',
  push: 'stats.overview.sources.push',
  search: 'stats.overview.sources.search',
  author_page: 'stats.overview.sources.authorPage',
};

function sourceName(code: string, t: ArticlesT): string {
  const named = SOURCE_NAMES[code];
  if (named !== undefined) {
    return t(named);
  }
  const words = code.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Where readers arrived from - bars, once the apps say. Not measured, it is a name in the quiet line;
 * measured with nothing recorded, it says so; the address a reader came from is never kept.
 */
function Sources({ now }: { readonly now: Dashboard }) {
  const { t } = useTranslation('articles');
  const sources = now.tiles.sources;
  if (sources.state === 'not_reported_yet') {
    return null;
  }
  const items = sources.items ?? [];
  const top = Math.max(1, ...items.map((item) => item.rawOpens));
  return (
    <StatCard title={t('stats.overview.sourcesTitle')}>
      {sources.state === 'no_data' || items.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t('stats.overview.noSources')}
        </Text>
      ) : (
        <BarList
          label={t('stats.overview.sourcesTitle')}
          bars={items.map((item) => ({
            key: item.surface,
            label: sourceName(item.surface, t),
            value: counted(item.rawOpens, 'views', t),
            share: item.rawOpens / top,
          }))}
        />
      )}
    </StatCard>
  );
}

/* ------------------------------------------------------------------- what each figure means */

/*
 * ★ THE (i) NOTES (design 24, T15; the teammate: "it is not clear what this metric is; add info to
 * all of them so it is clear what and how"; the owner, 2026-10-11: "include"). One sentence a
 * figure, from its own definition in the contract (authors-api.yaml, `getDashboard`'s tiles), in
 * plain words. A headline figure is a button, and a button cannot hold another, so each one's (i)
 * is drawn beside its button, in the switch's cell (`SwitchFigure.info`, the kit's figures.tsx);
 * every row of "Also in these N days" has its own, after its note. The Articles tab's day-by-day
 * figures open the same sentences through the same (i) (./statistics/ArticlesPanel.tsx; batch 2,
 * B2-INFO), so each figure is explained in one place.
 */

/* The sentences are the catalogue's `stats.info`, one per figure. */

/** A small (i) that opens what a figure means. */
export function FigureInfo({
  name,
  children,
}: {
  readonly name: string;
  readonly children: ReactNode;
}) {
  const { t } = useTranslation('articles');
  return (
    <Popover width={300} position="top" withArrow shadow="md">
      <Popover.Target>
        {/* A 24px target around the 14px icon, as the headline figures' (i) has (WCAG 2.5.8). */}
        <Anchor
          component="button"
          type="button"
          c="dimmed"
          lh={0}
          aria-label={t('stats.info.about', { name })}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 24,
            minHeight: 24,
          }}
        >
          <Info size={14} aria-hidden />
        </Anchor>
      </Popover.Target>
      <Popover.Dropdown>
        <Text size="xs" component="div">
          {children}
        </Text>
      </Popover.Dropdown>
    </Popover>
  );
}

/** A row of "Also in these N days" with its (i) after its note. */
function withInfo(figure: ListedFigure, info: string): ListedFigure {
  return {
    ...figure,
    note: (
      <>
        {figure.note}
        <FigureInfo name={figure.label}>{info}</FigureInfo>
      </>
    ),
  };
}
